import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Contact from './components/Contact';
import NewBuilds from './pages/NewBuilds';
import Renovations from './pages/Renovations';
import PoolsAndLandscapes from './pages/PoolsAndLandscapes';
import ScrollToTop from './components/ScrollToTop';
import { SiteConfig, defaultConfig } from './config/siteConfig';
import { ConfigService } from './config/configService';

function HomePage({ config }: { config: SiteConfig }) {
  return (
    <>
      <Hero businessName={config.business.name} />
      <About 
        title={config.about.title}
        content={config.about.content}
        mission={config.about.mission}
        vision={config.about.vision}
        experience={config.about.experience}
        image={config.about.image}
        stats={config.about.stats}
      />
      <Services 
        title={config.services.title}
        items={[
          {
            title: "New Builds",
            description: "From concept to completion, we construct custom homes and commercial spaces tailored to your needs and lifestyle.",
            image: "/images/new-builds/build-01.jpg",
            link: "/new-builds"
          },
          {
            title: "Renovations",
            description: "Breathe new life into your property with our expert renovation and extension services, designed to maximize comfort and value.",
            image: "/images/renovations/reno-01.jpg",
            link: "/renovations"
          },
          {
            title: "Landscaping & Pools",
            description: "Enhance your outdoor living with bespoke landscaping, pool design, and garden transformations.",
            image: "/images/pool-landscape/pool-01.jpg",
            link: "/pools-and-landscapes"
          }
        ]}
      />
      <Contact 
        title={config.contact.title}
        description={config.contact.description}
      />
    </>
  );
}

function App() {
  const [config, setConfig] = useState<SiteConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const configService = ConfigService.getInstance();
        const fetchedConfig = await configService.fetchConfig();
        setConfig(fetchedConfig);
        setError(null);
      } catch (err) {
        setError('Failed to load configuration');
        console.error('Error loading config:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-2xl mb-4">⚠️</div>
          <p className="text-gray-800">{error}</p>
          <p className="text-gray-600 mt-2">Using default configuration</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
    <Layout>
        <Routes>
          <Route path="/" element={<HomePage config={config} />} />
          <Route path="/new-builds" element={<NewBuilds />} />
          <Route path="/renovations" element={<Renovations />} />
          <Route path="/pools-and-landscapes" element={<PoolsAndLandscapes />} />
        </Routes>
    </Layout>
    </BrowserRouter>
  );
}

export default App; 